import { BookOpen, Check, ExternalLink, Trash } from "lucide-react";
import React, {
  FormEventHandler,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button,
  InlineSpinner,
  InputWithButton,
  LoadingErrorState,
  PageSkeleton,
} from "../../../components";
import { useApi, useAuth, useTranslations } from "../../../contexts";
import { client } from "../../../helpers";
import { AsyncStatus } from "../../../helpers/asyncStatus";
import { Domain } from "../../../types";
import { routerPaths } from "../../routerPaths";

const dnsProviderGuides = {
  cloudflare: {
    name: "Cloudflare",
    hrefKey: "CNAME doc links - Cloudflare",
  },
  godaddy: {
    name: "GoDaddy",
    hrefKey: "CNAME doc links - GoDaddy",
  },
  namecheap: {
    name: "Namecheap",
    hrefKey: "CNAME doc links - Namecheap",
  },
  squarespace: {
    name: "Squarespace",
    hrefKey: "CNAME doc links - Squarespace",
  },
  ionos: {
    name: "IONOS",
    hrefKey: "CNAME doc links - Ionos",
  },
  ovhcloud: {
    name: "OVHcloud",
    hrefKey: "CNAME doc links - OVH",
  },
} as const;

type DnsProvider = keyof typeof dnsProviderGuides | "custom";
type SupportedDnsProvider = keyof typeof dnsProviderGuides;

const DomainStatus: React.FC<{
  name: string;
  onDnsProviderChange: (provider: DnsProvider) => void;
}> = ({ name, onDnsProviderChange }) => {
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [requestStatus, setRequestStatus] = useState<AsyncStatus>("idle");
  const auth = useAuth();
  const i18n = useTranslations();

  const fetchDomainStatus = async (force = false) => {
    if ((!force && requestStatus === "loading") || !auth.token) return;
    setRequestStatus("loading");
    setError(null);
    try {
      const results = await (
        await client.api.domains[":name"].status.$get(
          {
            param: { name },
          },
          { headers: { authorization: `Bearer ${auth.token}` } }
        )
      ).json();
      if (results.success) {
        setStatus(results.data.domain.status);
        onDnsProviderChange(results.data.domain.dns_provider);
        if (results.data.domain.verification_errors) {
          console.error(results.data.domain.verification_errors);
          setError(i18n["Domain verification failed."]);
        }
        setRequestStatus("success");
      } else {
        setRequestStatus("error");
        setError(i18n["Could not load domain status"]);
        onDnsProviderChange("custom");
      }
    } catch (error) {
      console.error(error);
      setRequestStatus("error");
      setError(i18n["Could not load domain status"]);
      onDnsProviderChange("custom");
    }
  };

  useEffect(() => {
    fetchDomainStatus();
  }, [auth.token, name]);

  if (requestStatus === "loading") {
    return (
      <InlineSpinner
        className="me-2 text-slate-500"
        size="sm"
        label={i18n["Loading..."]}
      />
    );
  }
  if (requestStatus === "error") {
    return (
      <button
        type="button"
        onClick={() => fetchDomainStatus(true)}
        className="text-xs text-link hover:underline me-2"
      >
        {i18n["Retry"]}
      </button>
    );
  }
  if (error) return <span className="text-warning me-2 text-xs">{error}</span>;

  return status === "active" ? (
    <Check className="text-green-500 me-2" />
  ) : (
    <InlineSpinner
      className="me-2 text-slate-500"
      size="sm"
      label={i18n["Loading..."]}
    />
  );
};

const DnsProviderGuide: React.FC<{
  provider: SupportedDnsProvider;
}> = ({ provider }) => {
  const i18n = useTranslations();

  const guide = dnsProviderGuides[provider];
  return (
    <a
      href={i18n[guide.hrefKey]}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${guide.name} ${i18n["Setup guide"]}`}
      className="group inline-flex items-center gap-3 rounded-md text-xl font-bold text-link underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2"
    >
      <span>{guide.name}</span>
      <span className="text-base font-semibold text-slate-600">
        {i18n["Setup guide"]}
      </span>
      <ExternalLink
        aria-hidden="true"
        className="h-5 w-5 text-slate-400 transition-colors group-hover:text-sky-600"
      />
    </a>
  );
};

const DomainItem: React.FC<{
  domain: Domain;
  siteId: string;
  onDnsProviderChange: (provider: DnsProvider) => void;
}> = (props) => {
  const isZuvDomain = /\.zuv\.[^.]+$/.test(props.domain.name);
  const auth = useAuth();
  const i18n = useTranslations();
  const api = useApi();
  const [deleteStatus, setDeleteStatus] = useState<AsyncStatus>("idle");
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const headers = useMemo(
    () => ({
      authorization: `Bearer ${auth.token}`,
    }),
    [auth.token]
  );

  const deleteDomain = async () => {
    if (deleteStatus === "loading") return;
    if (!window.confirm(i18n["Are you sure?"])) {
      return;
    }
    setDeleteStatus("loading");
    setDeleteError(null);
    try {
      const results = await (
        await client.api.domains[":name"].$delete(
          {
            param: { name: props.domain.name },
          },
          { headers }
        )
      ).json();
      if (!results.success) {
        setDeleteStatus("error");
        console.error(results.error.issues[0].message);
        setDeleteError(i18n["Something went wrong"]);
        console.error(results.error);
        toast.error(i18n["Something went wrong"]);
      } else {
        setDeleteStatus("success");
        toast.success(i18n["Domain deleted"]);
        api.queryClient.invalidateQueries({
          queryKey: ["site", props.siteId],
        });
      }
    } catch (error) {
      console.error(error);
      setDeleteStatus("error");
      setDeleteError(i18n["Something went wrong"]);
      toast.error(i18n["Something went wrong"]);
    }
  };

  if (isZuvDomain) {
    return (
      <div className="flex items-center p-4">
        <span className="flex grow items-center">
          <Check className="text-green-500 me-2" />
          <a
            className="underline underline-offset-2"
            href={`https://${props.domain.name}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {props.domain.name}
          </a>
        </span>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center p-4">
        <span className="flex grow">
          <DomainStatus
            name={props.domain.name}
            onDnsProviderChange={props.onDnsProviderChange}
          />
          <a
            className="underline underline-offset-2"
            href={`https://${props.domain.name}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {props.domain.name}
          </a>
        </span>
        <Button
          variant="destructive"
          size="sm"
          loading={deleteStatus === "loading"}
          icon={deleteStatus === "loading" ? undefined : <Trash />}
          onClick={() => {
            deleteDomain();
          }}
        />
      </div>
      {deleteError && (
        <LoadingErrorState
          className="mx-3 mb-3"
          message={deleteError}
          retryLabel={i18n["Retry"]}
          onRetry={() => {
            deleteDomain();
          }}
        />
      )}
      <div className="m-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="border-b border-slate-200 pb-3 text-lg font-semibold text-slate-950">
          {i18n["DNS records"]}
        </h3>
        <div className="mt-4 space-y-3 text-sm text-slate-700">
          <p className="overflow-x-auto rounded-md bg-slate-50 px-3 py-2">
            <code>{`_acme-challenge.${props.domain.name} → ${props.domain.name}.c6e6fab17a483c2b.dcv.cloudflare.com`}</code>
          </p>
          <p className="overflow-x-auto rounded-md bg-slate-50 px-3 py-2">
            <code>{`${props.domain.name} → zuv.app`}</code>
          </p>
        </div>
      </div>
    </div>
  );
};

const AddDomainForm: React.FC<{ siteId: string }> = ({ siteId }) => {
  const [domain, setDomain] = useState("");
  const [submitStatus, setSubmitStatus] = useState<AsyncStatus>("idle");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const auth = useAuth();
  const i18n = useTranslations();
  const api = useApi();

  const addDomain = async (domainName: string) => {
    if (!domainName.trim() || submitStatus === "loading") return;
    setSubmitStatus("loading");
    setSubmitError(null);
    try {
      const results = await (
        await client.api.sites[":siteId"].domains.$post(
          {
            json: { domain: domainName },
            param: { siteId },
          },
          { headers: { authorization: `Bearer ${auth.token}` } }
        )
      ).json();
      if (!results.success) {
        setSubmitStatus("error");
        setSubmitError(i18n["Something went wrong"]);
        toast.error(i18n["Something went wrong"]);
      } else {
        setSubmitStatus("success");
        api.queryClient.invalidateQueries({
          queryKey: ["site", siteId],
        });
        setDomain("");
      }
    } catch (error) {
      console.error(error);
      setSubmitStatus("error");
      setSubmitError(i18n["Something went wrong"]);
      toast.error(i18n["Something went wrong"]);
    }
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    await addDomain(domain);
  };

  return (
    <form onSubmit={handleSubmit} aria-busy={submitStatus === "loading"}>
      <InputWithButton
        inputProps={{
          className: "grow",
          variant: "outlined",
          type: "text",
          placeholder: i18n.Domain,
          value: domain,
          disabled: submitStatus === "loading",
          onChange: (event) => setDomain(event.target.value),
        }}
        buttonProps={{
          label: "add",
          type: "submit",
          loading: submitStatus === "loading",
          disabled: !domain.trim() || submitStatus === "loading",
        }}
      />
      {submitError && (
        <LoadingErrorState
          className="mt-3"
          message={submitError}
          retryLabel={i18n["Retry"]}
          onRetry={() => addDomain(domain)}
        />
      )}
    </form>
  );
};

export const DomainsTab: React.FC = () => {
  const { siteId } =
    useParams<Parameters<typeof routerPaths.siteManage>["0"]>();
  if (!siteId) throw new Error(`no siteId in url params`);
  const api = useApi();
  const i18n = useTranslations();
  const siteQuery = api.siteById({ siteId });
  const site = siteQuery?.data;
  const [dnsProviders, setDnsProviders] = useState<
    Record<string, DnsProvider | null>
  >({});

  if (!siteQuery || siteQuery.isPending || siteQuery.isLoading) {
    return (
      <PageSkeleton
        cards={2}
        ariaLabel={i18n["Loading page content"]}
        loadingLabel={i18n["Loading..."]}
      />
    );
  }
  if (siteQuery.isError) {
    return (
      <LoadingErrorState
        message={i18n["Failed to load domains."]}
        retryLabel={i18n["Retry"]}
        onRetry={() => siteQuery.refetch()}
      />
    );
  }
  if (!site) {
    return (
      <LoadingErrorState
        message={i18n["Could not load site domains."]}
        retryLabel={i18n["Retry"]}
        onRetry={() => siteQuery.refetch()}
      />
    );
  }

  return (
    <div id="site-manage-domains-tab" className="pb-4" aria-busy={siteQuery.isFetching}>
      {site?.domains?.length ? (
        <div className="bg-slate-100 border rounded mb-4">
          {site.domains.map((domain) => (
            <DomainItem
              key={domain.name}
              domain={domain}
              siteId={siteId}
              onDnsProviderChange={(provider) =>
                setDnsProviders((current) =>
                  current[domain.name] === provider
                    ? current
                    : { ...current, [domain.name]: provider }
                )
              }
            />
          ))}
        </div>
      ) : null}
      <AddDomainForm siteId={siteId} />
      <div className="border mt-6" />
      <DomainInfo domains={site.domains || []} dnsProviders={dnsProviders} />
    </div>
  );
};

const DomainInfo: React.FC<{
  domains: Domain[];
  dnsProviders: Record<string, DnsProvider | null>;
}> = ({ domains, dnsProviders }) => {
  const i18n = useTranslations();
  const customDomains = domains.filter(
    (domain) => !/\.zuv\.[^.]+$/.test(domain.name)
  );
  const providerGuides = customDomains.flatMap((domain) => {
    const provider = dnsProviders[domain.name];
    return provider && provider !== "custom" ? [{ domain, provider }] : [];
  });

  return (
    <section
      className="mt-6 overflow-hidden rounded-xl border border-sky-200 bg-sky-50 shadow-sm"
      aria-labelledby="domain-setup-title"
    >
      <div className="p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-600 text-white shadow-sm">
            <BookOpen aria-hidden="true" className="h-5 w-5" />
          </span>
          <div>
            <h2
              id="domain-setup-title"
              className="text-lg font-semibold text-slate-950"
            >
              {i18n["Domain setup"]}
            </h2>
            <p className="mt-1 text-sm leading-6 text-slate-600">
              {
                i18n[
                  "A domain is the unique web address (e.g. yourwebsite.com) that people use to access your site."
                ]
              }
            </p>
          </div>
        </div>

        {providerGuides.length ? (
          <div className="mt-5 space-y-3">
            {providerGuides.map(({ domain, provider }) => (
              <div
                key={domain.name}
                className="rounded-lg border border-sky-200 bg-white px-4 py-4 shadow-sm"
              >
                {customDomains.length > 1 ? (
                  <p className="mb-2 text-sm font-semibold text-slate-700">
                    {domain.name}
                  </p>
                ) : null}
                <p className="mb-2 text-sm font-bold uppercase tracking-wide text-sky-700">
                  {i18n["DNS provider"]}
                </p>
                <DnsProviderGuide provider={provider} />
              </div>
            ))}
          </div>
        ) : null}

        <p className="mt-5 rounded-lg border border-sky-200 bg-sky-100/70 px-4 py-3 text-sm font-medium text-sky-950">
          {
            i18n[
              "Add both DNS records at your DNS provider. Choose CNAME as the record type."
            ]
          }
        </p>
      </div>

      <div className="flex flex-col items-start justify-between gap-3 border-t border-sky-200 bg-white/70 px-5 py-4 sm:flex-row sm:items-center sm:px-6">
        <p className="text-sm text-slate-600">
          {i18n["If you encounter any difficulties, please contact us"]}.
        </p>
        <Button linkTo="https://cal.com/florianherrengt/zuv" size="sm">
          {i18n["Book Appointment"]}
        </Button>
      </div>
    </section>
  );
};
